import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, map } from 'rxjs';
import { IDigitalProductBillingType } from '../../../digitalProduct/models/digitalProductBillingType.model';
import { IDigitalProductRecurringInterval } from '../../../digitalProduct/models/digitalProductRecurringInterval.model';
import { IDigitalProductVisibility } from '../../../digitalProduct/models/digitalProductVisibility.model';
import { IDigitalProduct } from '../../../digitalProduct/models/digitalProduct.model';
import { SubscriptionService } from '../../services/subscription.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'clp-subscription-public',
    templateUrl: './subscriptionPublic.component.html',
    styleUrls: ['./subscriptionPublic.component.scss'],
    imports: [
        CommonModule,
        RouterModule,
        TranslateModule
    ]
})
export class SubscriptionPublicComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private subscriptionService = inject(SubscriptionService);

    subscription: IDigitalProduct | null = null;
    loading = true;
    notFound = false;
    hasError = false;
    userId = '';
    slug = '';
    private readonly billingTypeMap: Record<string, IDigitalProductBillingType> = {
        '2': IDigitalProductBillingType.Recurring,
        'Recurring': IDigitalProductBillingType.Recurring
    };
    private readonly recurringIntervalMap: Record<string, IDigitalProductRecurringInterval> = {
        '0': IDigitalProductRecurringInterval.None,
        'None': IDigitalProductRecurringInterval.None,
        '1': IDigitalProductRecurringInterval.Day,
        'Day': IDigitalProductRecurringInterval.Day,
        '2': IDigitalProductRecurringInterval.Week,
        'Week': IDigitalProductRecurringInterval.Week,
        '3': IDigitalProductRecurringInterval.Month,
        'Month': IDigitalProductRecurringInterval.Month,
        '4': IDigitalProductRecurringInterval.Year,
        'Year': IDigitalProductRecurringInterval.Year
    };
    private readonly visibilityMap: Record<string, IDigitalProductVisibility> = {
        '1': IDigitalProductVisibility.Draft,
        'Draft': IDigitalProductVisibility.Draft,
        '2': IDigitalProductVisibility.Published,
        'Published': IDigitalProductVisibility.Published,
        '3': IDigitalProductVisibility.Archived,
        'Archived': IDigitalProductVisibility.Archived
    };

    ngOnInit(): void {
        this.route.paramMap.pipe(
            map(params => ({
                userId: (params.get('userId') ?? '').trim(),
                slug: (params.get('slug') ?? '').trim()
            })),
            distinctUntilChanged((previous, current) =>
                previous.userId === current.userId && previous.slug === current.slug)
        ).subscribe(params => {
            this.userId = params.userId;
            this.slug = params.slug;
            this.loadSubscription();
        });
    }

    reload(): void {
        this.loadSubscription();
    }

    isRecurringProduct(subscription: IDigitalProduct): boolean {
        return this.resolveBillingType(subscription.billingType) === IDigitalProductBillingType.Recurring;
    }

    getPublicHandle(): string {
        return `@${this.userId}`;
    }

    getBillingTypeLabelKey(billingType: unknown): string {
        return `subscription.billingType.${this.resolveBillingType(billingType) ?? IDigitalProductBillingType.Recurring}`;
    }

    getVisibilityLabelKey(visibility: unknown): string {
        return `subscription.visibility.${this.resolveVisibility(visibility) ?? IDigitalProductVisibility.Draft}`;
    }

    getRecurringIntervalLabelKey(recurringInterval: unknown): string {
        return `subscription.recurringInterval.${this.resolveRecurringInterval(recurringInterval) ?? IDigitalProductRecurringInterval.None}`;
    }

    getVisibilityBadgeClass(visibility: unknown): string {
        switch (this.resolveVisibility(visibility)) {
            case IDigitalProductVisibility.Published:
                return 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300';
            case IDigitalProductVisibility.Archived:
                return 'border-amber-500/40 bg-amber-500/15 text-amber-300';
            case IDigitalProductVisibility.Draft:
            default:
                return 'border-slate-600 bg-slate-700/50 text-slate-200';
        }
    }

    getBillingTypeBadgeClass(billingType: unknown): string {
        switch (this.resolveBillingType(billingType)) {
            case IDigitalProductBillingType.Recurring:
                return 'border-sky-500/40 bg-sky-500/15 text-sky-300';
            default:
                return 'border-sky-500/40 bg-sky-500/15 text-sky-300';
        }
    }

    getPublishedDate(value: Date | string | null | undefined): Date | null {
        if (!value) {
            return null;
        }

        const dateValue = value instanceof Date ? value : new Date(value);
        return Number.isNaN(dateValue.getTime()) ? null : dateValue;
    }

    resolveCoverImageUrl(coverUrl?: string | null): string | undefined {
        const normalizedCoverUrl = coverUrl?.trim();
        if (!normalizedCoverUrl) {
            return undefined;
        }

        if (normalizedCoverUrl.startsWith('http://') ||
            normalizedCoverUrl.startsWith('https://') ||
            normalizedCoverUrl.startsWith('data:') ||
            normalizedCoverUrl.startsWith('blob:')) {
            return normalizedCoverUrl;
        }

        const sanitizedCoverUrl = normalizedCoverUrl.replace(/\\/g, '/');

        if (sanitizedCoverUrl.startsWith('/products/images/')) {
            return `${environment.API_URL}${sanitizedCoverUrl}`;
        }

        const fileName = sanitizedCoverUrl.split('/').filter(Boolean).pop();
        if (!fileName) {
            return undefined;
        }

        return `${environment.API_URL}/products/images/${fileName}`;
    }

    private loadSubscription(): void {
        this.loading = true;
        this.hasError = false;
        this.notFound = false;
        this.subscription = null;

        this.subscriptionService.getPublicSubscriptionByUserAndSlug(this.userId, this.slug).subscribe({
            next: subscription => {
                this.loading = false;

                if (!subscription?.id) {
                    this.notFound = true;
                    return;
                }

                this.subscription = subscription;
            },
            error: () => {
                this.loading = false;
                this.hasError = true;
            }
        });
    }

    private resolveBillingType(value: unknown): IDigitalProductBillingType | undefined {
        return this.resolveEnumValue(value, this.billingTypeMap) ?? IDigitalProductBillingType.Recurring;
    }

    private resolveVisibility(value: unknown): IDigitalProductVisibility | undefined {
        return this.resolveEnumValue(value, this.visibilityMap);
    }

    private resolveRecurringInterval(value: unknown): IDigitalProductRecurringInterval | undefined {
        return this.resolveEnumValue(value, this.recurringIntervalMap);
    }

    private resolveEnumValue<T extends number>(value: unknown, map: Record<string, T>): T | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        return map[String(value).trim()];
    }
}
